// Destructible terrain: interior ground blocks take damage from player shots and
// explosions. Every hit flashes the block and kicks up dust; damage darkens it and
// grows cracks (3 stages); at 0 HP it bursts into debris and RoomMap removes the
// cell (collision, pathing and loot all read the same cell grid, so they follow).
// The outer frame, doors and floors under props never break (RoomMap.isBreakable);
// hits on them still flash so the world reacts to every shot.

import Phaser from 'phaser';
import { DEPTH, TILE } from '../../config/constants';
import { COLORS, mix } from '../../config/palette';
import type { GameContext } from '../../core/contracts';
import { CELL } from './template';
import type { RoomMap } from './tilemap';

/** Block hit points (a pulse carbine needs ~8 shots; most explosions break 1–3 blocks). */
export const TILE_HP = 90;
/** Explosions hit terrain harder than they hit armour. */
const EXPLOSION_MULT = 1.4;
/** Hit flash length (s). */
const FLASH_TIME = 0.12;
/** Minimum time between dust puffs / tint updates on one block (fast weapons). */
const PUFF_COOLDOWN = 0.06;
/** Darkest tint of a nearly broken block. */
const WORN_TINT = 0x6f7480;

interface Flash {
  i: number;
  t: number;
}

export class Destructible {
  private readonly damage: Float32Array;
  /** Crack stage drawn per cell (0 = none). */
  private readonly stage: Uint8Array;
  private readonly lastPuff: Float32Array;
  private readonly cracked = new Set<number>();
  private readonly crackG: Phaser.GameObjects.Graphics;
  private readonly flashG: Phaser.GameObjects.Graphics;
  private flashes: Flash[] = [];
  private time = 0;
  private destroyed = false;

  constructor(
    private readonly ctx: GameContext,
    private readonly map: RoomMap,
  ) {
    const n = map.cols * map.rows;
    this.damage = new Float32Array(n);
    this.stage = new Uint8Array(n);
    this.lastPuff = new Float32Array(n).fill(-1);
    const scene = ctx.scene;
    this.crackG = scene.add.graphics().setDepth(DEPTH.TILES + 1);
    this.flashG = scene.add.graphics().setDepth(DEPTH.TILES + 2).setBlendMode(Phaser.BlendModes.ADD);
  }

  /** Damage the block containing (x, y). Non-breakable solids only react. Returns true if it broke. */
  hitAt(x: number, y: number, amount: number): boolean {
    if (this.destroyed || !(amount > 0)) return false;
    const tx = Math.floor(x / TILE);
    const ty = Math.floor(y / TILE);
    return this.hitCell(tx, ty, amount, x, y);
  }

  /** Explosion: damage every block whose centre lies within the radius (with falloff). */
  hitRadius(x: number, y: number, radius: number, amount: number): void {
    if (this.destroyed || !(radius > 0) || !(amount > 0)) return;
    const reach = radius + TILE * 0.5;
    const tx0 = Math.floor((x - reach) / TILE);
    const tx1 = Math.floor((x + reach) / TILE);
    const ty0 = Math.floor((y - reach) / TILE);
    const ty1 = Math.floor((y + reach) / TILE);
    for (let ty = ty0; ty <= ty1; ty++) {
      for (let tx = tx0; tx <= tx1; tx++) {
        const cx = (tx + 0.5) * TILE;
        const cy = (ty + 0.5) * TILE;
        const d = Math.hypot(cx - x, cy - y);
        if (d > reach) continue;
        const f = 1 - 0.6 * Math.min(1, d / reach);
        this.hitCell(tx, ty, amount * EXPLOSION_MULT * f, cx, cy);
      }
    }
  }

  update(dt: number): void {
    if (this.destroyed) return;
    this.time += dt;
    if (this.flashes.length === 0) return;
    const g = this.flashG;
    g.clear();
    const cols = this.map.cols;
    let w = 0;
    for (const f of this.flashes) {
      f.t -= dt;
      if (f.t <= 0) continue;
      this.flashes[w++] = f;
      const k = f.t / FLASH_TIME;
      g.fillStyle(0xffffff, 0.4 * k * k);
      g.fillRect((f.i % cols) * TILE, Math.floor(f.i / cols) * TILE, TILE, TILE);
    }
    this.flashes.length = w;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.flashes = [];
    this.crackG.destroy();
    this.flashG.destroy();
  }

  // ── Internals ──

  private hitCell(tx: number, ty: number, amount: number, hx: number, hy: number): boolean {
    const m = this.map;
    if (tx < 0 || ty < 0 || tx >= m.cols || ty >= m.rows) return false;
    if (m.kindAtTile(tx, ty) !== CELL.SOLID) return false;
    const i = ty * m.cols + tx;
    this.flash(i);
    const puff = this.time - this.lastPuff[i] >= PUFF_COOLDOWN;
    if (puff) this.lastPuff[i] = this.time;
    if (!m.isBreakable(tx, ty)) return false;

    this.damage[i] += amount;
    if (this.damage[i] >= TILE_HP) {
      this.breakBlock(tx, ty, i);
      return true;
    }
    const ratio = this.damage[i] / TILE_HP;
    // Wear: darker as it weakens.
    const tile = m.solids.getTileAt(tx, ty);
    if (tile) tile.tint = mix(0xffffff, WORN_TINT, ratio);
    const s = ratio >= 0.75 ? 3 : ratio >= 0.5 ? 2 : ratio >= 0.25 ? 1 : 0;
    if (s !== this.stage[i]) {
      this.stage[i] = s;
      this.cracked.add(i);
      this.drawCracks();
    }
    if (puff) {
      try {
        this.ctx.fx.sparks(hx, hy, -Math.PI / 2, { count: 2, color: mix(COLORS.steel4, 0xffffff, 0.2), speed: 160, spread: 2.2, life: 220 });
      } catch {
        // Cosmetic only.
      }
    }
    return false;
  }

  private flash(i: number): void {
    const f = this.flashes.find((q) => q.i === i);
    if (f) f.t = FLASH_TIME;
    else this.flashes.push({ i, t: FLASH_TIME });
  }

  private breakBlock(tx: number, ty: number, i: number): void {
    if (!this.map.breakCell(tx, ty)) return;
    this.damage[i] = 0;
    this.stage[i] = 0;
    if (this.cracked.delete(i)) this.drawCracks();
    this.flashes = this.flashes.filter((f) => f.i !== i);
    const cx = (tx + 0.5) * TILE;
    const cy = (ty + 0.5) * TILE;
    const { fx, audio } = this.ctx;
    try {
      fx.debris(cx, cy, { count: 9, color: COLORS.steel3, speed: 340 });
      fx.smoke(cx, cy, { count: 4, scale: 0.75 });
      fx.sparks(cx, cy, -Math.PI / 2, { count: 6, color: COLORS.steel5, speed: 300, spread: Math.PI, life: 260 });
      fx.shake(0.0035, 110);
      audio.play('land_heavy', { volume: 0.7, pitch: 0.75 + Math.random() * 0.15 });
    } catch {
      // Cosmetic only.
    }
  }

  /** Redraw every crack (only when a block changes stage, so this stays rare). */
  private drawCracks(): void {
    const g = this.crackG;
    g.clear();
    const cols = this.map.cols;
    for (const i of this.cracked) {
      const s = this.stage[i];
      if (s === 0) continue;
      const x0 = (i % cols) * TILE;
      const y0 = Math.floor(i / cols) * TILE;
      // Deterministic per block: the same cracks extend as it weakens.
      let seed = (i * 2654435761) >>> 0;
      const rnd = () => {
        seed = (seed * 1664525 + 1013904223) >>> 0;
        return seed / 4294967296;
      };
      const ox = x0 + TILE * (0.35 + rnd() * 0.3);
      const oy = y0 + TILE * (0.35 + rnd() * 0.3);
      const branches = 1 + s;
      for (let b = 0; b < branches; b++) {
        const pts: number[] = [ox, oy];
        let a = rnd() * Math.PI * 2;
        let px = ox;
        let py = oy;
        const segs = 1 + s;
        for (let k = 0; k < segs; k++) {
          a += (rnd() - 0.5) * 1.2;
          const len = TILE * (0.12 + rnd() * 0.12);
          px = Phaser.Math.Clamp(px + Math.cos(a) * len, x0 + 2, x0 + TILE - 2);
          py = Phaser.Math.Clamp(py + Math.sin(a) * len, y0 + 2, y0 + TILE - 2);
          pts.push(px, py);
        }
        // Dark crack with a faint lit lip.
        g.lineStyle(2.5, COLORS.ink, 0.9);
        g.beginPath();
        g.moveTo(pts[0], pts[1]);
        for (let p = 2; p < pts.length; p += 2) g.lineTo(pts[p], pts[p + 1]);
        g.strokePath();
        g.lineStyle(1, COLORS.steel5, 0.35);
        g.beginPath();
        g.moveTo(pts[0] + 1, pts[1] + 1);
        for (let p = 2; p < pts.length; p += 2) g.lineTo(pts[p] + 1, pts[p + 1] + 1);
        g.strokePath();
      }
    }
  }
}
